import React, { useState } from "react";
import { Calendar, Clock } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Offer } from "../../../types/modules/offer.type";
import { OfferType } from "../../../types/enum/offersType";
import ToggleSwitch from "../../ui/ToggleSwitch";
import { useUpdateResource } from "../../../hooks/useUpdateResource";
import { formatNumber } from "../../../utils/formatNumber";
import { CustomDate } from "../../../utils/day";

type Props = {
  offer: Offer;
  storeId?: number;
};

const OfferListItem: React.FC<Props> = ({ offer, storeId }) => {
  const { t } = useTranslation();

  // --- Format Discount ---
  const formatDiscount = () => {
    let text = "";
    switch (offer.type) {
      case OfferType.FIXED:
        text = `${formatNumber(offer.discountAmount??0)}`;
        break;
      case OfferType.PERCENTAGE:
        text = `${formatNumber(offer.discountPercentage??0)}%`;
        break;
      case OfferType.INCENTIVE:
        text = `${formatNumber(offer.buyQty??0)} + ${formatNumber(offer.getFreeQty??0)} ${t("free")}`;
        break;
      default:
        text = "-";
    }

    return (
      <div className="flex items-center justify-center gap-1 h-[32px] min-w-[73px] px-2 py-1 rounded text-white w-max bg-Cyne rounded-tl-[20px] rounded-tr-[8px] rounded-br-[20px] rounded-bl-[8px]">
        <img src="/money.png" alt="money" />
        <span className="font-semibold text-xs">{text}</span>
      </div>
    );
  };

  // --- Format Products ---
  const formatProducts = () => {
    if (offer.target.toLowerCase() === "all")
      return (
        <div className="text-[12px] font-medium text-Black">
          {t("on_all")}{" "}
          <span className="font-bold text-[14px] text-Cyne capitalize">
            {t("products")}
          </span>
        </div>
      );

    if (offer.target.toLowerCase() === "product") {
      return (
        <div className="flex items-center gap-2">
          {offer.products?.slice(0, 3).map((p) => (
            <img
              key={p.id}
              src={p.image}
              alt={`product-${p.id}`}
              className="w-10 h-8 rounded-md object-cover"
            />
          ))}
          {offer.moreProducts ? (
            <div className="w-10 h-8 rounded-md bg-Black bg-opacity-50 relative flex items-center justify-center text-white text-xs font-bold">
              <span className="absolute inset-0 bg-Black opacity-50 rounded-md"></span>
              <span className="relative z-10">+{offer.moreProducts}</span>
            </div>
          ) : null}
        </div>
      );
    }

    if (offer.target.toLowerCase() === "category" && offer.categories) {
      const titles = offer.categories
        .map((c) => c.languages[0].title)
        .join(" & ");
      return (
        <div className="text-[12px] font-medium text-Black">
          {t("on_category")}{" "}
          <span className="font-bold text-[14px] text-Cyne capitalize">
            {titles}
          </span>
        </div>
      );
    }
  };

  // --- Date/Duration ---
  const formatDateDuration = () => {
    const now = new Date();
    const end = new Date(offer.endDate);

    if (end < now) {
      return (
        <div className="flex items-center gap-1">
          <Calendar className="text-DarkGrey2" size={20} />
          <span>{CustomDate(end.toISOString().slice(0, 10))}</span>
        </div>
      );
    } else {
      const diff = end.getTime() - now.getTime();
      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
      const minutes = Math.floor((diff / (1000 * 60)) % 60);

      return (
        <div className="flex items-center gap-1">
          <Clock className="text-DarkGrey2" size={20} />
          <span>
            {formatNumber(days)}{t('d')} {formatNumber(hours)}{t('h')} {formatNumber(minutes)}{t('m')}
          </span>
        </div>
      );
    }
  };

  // --- Status ---
  const getStatus = () => {
    const now = new Date();
    const end = new Date(offer.endDate);
    if (end < now) return { label: t("ended"), bg: "bg-DarkGrey2" };
    if (offer.isActive) return { label: t("active"), bg: "bg-primaryButton" };
    return { label: t("waiting_approval"), bg: "bg-YellowSecondary" };
  };

  const status = getStatus();

    const [isActive,setIsActive] = useState(offer.isActive);
  
    const {updateResource} = useUpdateResource();

    async function handleChangeActive(){
        const newValue = !isActive;
        setIsActive(p=>!p);
        await  updateResource(`offer/active-status/${offer.id}`, {isActive:newValue}, "admin",{
            method: "PUT",
            invalidateQueries:["store-offers"],
        });
        }

  return (
    <tr
      key={offer.id}
      className="bg-white rounded-[12px]"
      style={{ boxShadow: "0px 4px 32px rgba(86, 92, 120, 0.08)" }}
    >
      <td className="p-3 min-w-[200px] font-black text-Black">{offer.name}</td>
      <td className="p-3 min-w-[200px]">{formatProducts()}</td>
      <td className="p-3 min-w-[180px] text-DarkGrey2 font-semibold">
        {formatDateDuration()}
      </td>
      <td className="p-3 min-w-[150px] font-semibold">{formatDiscount()}</td>
      <td className="p-3 min-w-[80px] font-black text-Black">
        {formatNumber(offer.usedCount)}
      </td>
      <td className="p-3 min-w-[140px]">
        <span
          className={`px-3 capitalize py-3 flex items-center w-fit justify-center rounded-tl-[20px] rounded-tr-[8px] rounded-br-[20px] rounded-bl-[8px] min-w-[90px] h-[32px] font-bold text-xs text-white ${status.bg}`}
        >
          {status.label}
        </span>
      </td>

      {storeId && (
        <td className="p-3 min-w-[200px] text-Black font-normal text-justify">
          <ToggleSwitch
            checked={isActive}
            onChange={handleChangeActive}
            size="sm"
          />
        </td>
      )}
    </tr>
  );
};

export default OfferListItem;
